/**
 * NotificationPreferences — per-channel push toggle page.
 *
 * Each row is one push channel (direct chat, commerce, price drop,
 * test). Toggling silently PUTs /api/push/preferences so the change
 * takes effect immediately for the next push_events row that gets
 * dispatched.
 *
 * Also embeds the browser push settings card at the top so a shopper
 * who lands here can enable OS-level notifications in the same flow.
 */
import * as React from "react";
import { motion } from "framer-motion";
import { Bell, MessageCircle, ShoppingBag, Tag, Zap, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PushNotificationsCard } from "@/components/push/PushNotificationsCard";

type Prefs = {
  direct_chat: boolean;
  commerce: boolean;
  price_drop: boolean;
  test: boolean;
};

const CHANNELS: { id: keyof Prefs; label: string; hint: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "direct_chat", label: "Buyer & seller chats",   hint: "Live messages from a seller (or buyer, if you sell).",             icon: MessageCircle },
  { id: "commerce",    label: "Sancta commerce",         hint: "Order placed, order modified, VIP unlocks, and payment updates.",  icon: ShoppingBag },
  { id: "price_drop",  label: "Price-drop alerts",       hint: "Postponed AI Cart™ items that drop by 5% or more.",                 icon: Tag },
  { id: "test",        label: "Test pings",              hint: "Manual /push/test dispatches — leave on unless you're debugging.", icon: Zap },
];

const NotificationPreferences: React.FC = () => {
  const [prefs, setPrefs] = React.useState<Prefs | null>(null);
  const [saving, setSaving] = React.useState<keyof Prefs | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ preferences: Prefs }>("/push/preferences");
        setPrefs(r.data?.preferences);
      } catch {
        toast.error("Couldn't load your preferences.");
      }
    })();
  }, []);

  async function toggle(id: keyof Prefs) {
    if (!prefs) return;
    setSaving(id);
    const next = { ...prefs, [id]: !prefs[id] };
    setPrefs(next);  // optimistic
    try {
      const r = await api.put<{ preferences: Prefs }>("/push/preferences", { [id]: next[id] });
      setPrefs(r.data?.preferences);
    } catch (e: any) {
      setPrefs(prefs); // revert
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-slate-950 text-neutral-900 dark:text-white">
      <header className="border-b border-neutral-200 dark:border-white/10 bg-white/70 dark:bg-slate-950/70 backdrop-blur px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-500 to-fuchsia-500 grid place-items-center text-white">
            <Bell className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-tight" data-testid="notif-prefs-title">
              Notification preferences
            </div>
            <div className="text-[11px] text-neutral-500 dark:text-slate-400">
              Fine-tune what pings you on your phone, your desktop, and inside SociBaba.
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <PushNotificationsCard />

        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-neutral-200 dark:border-white/10">
          <div className="px-4 py-3 border-b border-neutral-100 dark:border-white/10 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-neutral-500 dark:text-slate-400" />
            <div className="text-sm font-semibold">Per-channel toggles</div>
          </div>
          <ul className="divide-y divide-neutral-100 dark:divide-white/10">
            {CHANNELS.map(({ id, label, hint, icon: Icon }) => {
              const on = !!prefs?.[id];
              return (
                <li key={id} className="flex items-center gap-4 px-4 py-4">
                  <div className={`h-9 w-9 rounded-xl grid place-items-center ${on ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300" : "bg-neutral-100 text-neutral-400 dark:bg-white/5 dark:text-slate-500"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-[11px] text-neutral-500 dark:text-slate-400">{hint}</div>
                  </div>
                  <motion.button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    data-testid={`notif-toggle-${id}`}
                    disabled={!prefs || saving === id}
                    onClick={() => toggle(id)}
                    whileTap={{ scale: 0.94 }}
                    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                      on ? "bg-emerald-500" : "bg-neutral-300 dark:bg-white/15"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transform transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`}
                    />
                    {saving === id && (
                      <Loader2 className="absolute -right-6 top-1 h-3 w-3 animate-spin text-neutral-400" />
                    )}
                  </motion.button>
                </li>
              );
            })}
          </ul>
        </section>

        <p className="text-[11px] text-neutral-500 dark:text-slate-400 px-1">
          Silencing a channel doesn't lose the message — chats and orders still land in your inbox on SociBaba, we just skip the push notification.
        </p>
      </main>
    </div>
  );
};

export default NotificationPreferences;
